import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import type {
	ResultClickedCallback,
	ResultClickedCallbackParams,
} from '../../../common/constants/types';
import {
	onAdvancedSearchResultLinkContextMenuClicked,
	type ResultEventAttributes,
} from '../../../common/utils/events/advanced-search-result-interacted';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import type { SharedEventAttributes } from '../../analytics/prepared-result-attributes';
import type { AnalyticsEventCallbacks } from '../../analytics/result-analytics-callbacks';
import { usePrimaryProduct } from '../../store/bootstrap';

/**
 * TODO this whole file is a copy of useFireResultClicked, we should add a parameter to  useFireResultClicked to allow it to be used for both left and right clicks
 * while doing the above, it's good to move all the logic for 3P products to onAdvancedSearchResultLinkClicked and change its name to getAdvancedSearchResultClickedAnalyticsPayload
 */

/**
 * @returns a callback to fire an event when the result
 * conext menu is clicked by the user, i.e. they right
 * click a result.
 */

export const useFireResultCtxMenuClickedWithAttributes = (
	sharedEventAttributes: SharedEventAttributes,
	eventCallbacks: AnalyticsEventCallbacks,
	resultEventAttributes: ResultEventAttributes,
): ResultClickedCallback => {
	const { commonAttributes, nonPrivacySafeAttributes } = sharedEventAttributes;
	const { fireAnalyticsEvent } = eventCallbacks;
	return useCallback(
		(interactionAttributes: ResultClickedCallbackParams = {}) => {
			const analyticsPayload = onAdvancedSearchResultLinkContextMenuClicked({
				resultEventAttributes,
				interactionAttributes,
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
			});
			fireAnalyticsEvent(analyticsPayload);
		},
		[fireAnalyticsEvent, resultEventAttributes, commonAttributes, nonPrivacySafeAttributes],
	);
};

export const useFireResultCtxMenuClicked = (
	props: ResultEventAttributes,
): ResultClickedCallback => {
	const sharedEventAttributes = useCommonAndPrivateAttributes();
	const intl = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionTriggered = isIntentDetectionTriggeredForExperiment(
		primaryProduct,
		intl.locale,
	);
	return useFireResultCtxMenuClickedWithAttributes(
		sharedEventAttributes,
		{ fireAnalyticsEvent },
		{ ...props, intentDetectionTriggered },
	);
};
